import { Component, input, output, computed, inject, HostListener } from '@angular/core'
import { CommonModule } from '@angular/common'
import { Kanban } from '../../../../_services/kanban/kanban.model'
import { KanbanService } from '../../kanban.service'
import { TagChangeEvent, TagsConfig } from '../../models/kanban.model'

export type ListElement = {
  item: string
  disabled?: boolean
  reference?: string
  isAllowed?: boolean
  onClick: (card: Kanban.Card.Entity) => void
}

export type ActionsConfigProps = {
  primary?: ActionsConfig
  secondary?: ActionsConfig
  others?: ListElement[]
}

export type ActionsConfig = {
  label: string
  isAllowed?: boolean
  onClick: (card: Kanban.Card.Entity) => void
}

export type FieldConfig = {
  id: string
  pipe?: (value: Kanban.Card.CardField['value'] | undefined) => string
  label?: string
  value?: string
}

@Component({
  selector: 'app-kanban-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './kanban-card.component.html',
  styleUrl: './kanban-card.component.scss'
})
export class KanbanCardComponent {
  private kanbanService = inject(KanbanService)
  card = input.required<Kanban.Card.Entity>()
  actionsConfig = input<ActionsConfigProps>()
  fieldsToShow = input<FieldConfig[]>()
  tagsConfig = input.required<TagsConfig>()
  showMoreActions: boolean = false

  cardClick = output<Kanban.Card.Entity>()
  editCard = output<Kanban.Card.Entity>()
  deleteCard = output<string>()
  openPhaseForm = output<Kanban.Card.Entity>()
  tagSelected = output<TagChangeEvent>()
  tagRemoved = output<TagChangeEvent>()

  tags = computed(() => {
    return (
      this.card().tags.map(tag => {
        const currentTag = this.kanbanService.board()?.tags.find(t => t.id === tag.tagId)
        const canRemove = this.tagsConfig().allowEdit && currentTag?.type === 'dynamic'
        return { label: currentTag?.label || tag.tagId, id: tag.tagId, canRemove }
      }) || []
    )
  })

  availableTags = computed(() => {
    const boardTags = this.kanbanService.tags() || []
    const cardTagIds = this.card().tags.map(t => t.tagId)
    return boardTags
      .filter(tag => !cardTagIds.includes(tag.id) && tag.type === 'dynamic')
      .map(tag => ({
        value: tag.id,
        label: tag.label
      }))
  })

  showAddTag = computed(() => {
    return this.tagsConfig().allowEdit && this.availableTags().length > 0
  })

  protected dropdownActions = computed(() =>
    (this.actionsConfig()?.others ?? []).filter(action => action.isAllowed !== false)
  )

  protected phaseHasForm = computed(() => {
    const phases = this.kanbanService.phases()
    const phase = phases?.find(p => p.id === this.card().phaseId)
    return !!phase?.formSchema?.length
  })

  protected isFormPending = computed(() => {
    const card = this.card()
    return !card.phaseFormSubmissions?.[card.phaseId]
  })

  visibleCardFields = computed(() => {
    const boardFields = this.kanbanService.board()?.fields ?? []
    return this.fieldsToShow()
      ?.map((field: FieldConfig) => {
        const cardFieldValue = this.card().fields.find(f => f.fieldId === field.id)
        const pipe =
          field.pipe ?? ((value: Kanban.Card.CardField['value'] | undefined) => String(value ?? ''))
        const boardField = boardFields.find(f => f.id === field.id)
        return {
          ...field,
          label: field.label ?? boardField?.label,
          value: pipe(cardFieldValue?.value)
        }
      })
      .filter(field => !!field.value)
  })

  cardFields = computed(() => this.card().fields || [])

  onCardClick(event: Event): void {
    event?.stopPropagation()
    this.cardClick.emit(this.card())
  }

  toggleMoreActions(event: Event): void {
    event.stopPropagation()
    this.showMoreActions = !this.showMoreActions
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showMoreActions) {
      this.showMoreActions = false
    }
  }

  protected onDropdownActionClick(action: ListElement): void {
    this.showMoreActions = false
    action.onClick(this.card())
  }

  onTagRemoved(tagId: string) {
    this.tagRemoved.emit({ tagId, cardId: this.card().id })
  }

  onTagSelected(event: Event) {
    const select = event.target as HTMLSelectElement
    this.tagSelected.emit({ tagId: select.value, cardId: this.card().id })
    select.value = ''
  }
}
